/**
 * Per-client outstanding statement exports (PDF + Excel).
 *
 * Renders each invoice in its native currency using the invoice's own
 * amount fields. The display-currency toggle is intentionally NOT used
 * here: this is the document sent to the client, so the currency must
 * match what they were invoiced in.
 *
 * Single-currency client: header and totals in that currency.
 * Multi-currency client: Currency column, per-currency subtotals,
 * no cross-currency grand total.
 */
import { currencySymbol } from './chartFormatters';
import {
  drawBrandedHeader, drawBrandedFooter,
  TABLE_DEFAULTS, addFormattedSheet, EXCEL_CURRENCY_FORMAT,
} from './exportUtils';
import { invoicePaidAmount } from './invoicePaid';
import { deriveInvoiceStatus } from './utils';

export function getOutstandingInvoices(invoices) {
  return invoices.filter(inv => {
    const status = deriveInvoiceStatus(inv);
    // Outstanding = not fully paid, not cancelled, not void
    return status !== 'paid' && status !== 'cancelled' && status !== 'void';
  });
}

function slugifyClient(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export async function exportClientStatementPDF({ client, invoices }) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const outstanding = getOutstandingInvoices(invoices);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // Determine currencies present (native, not display toggle)
  const currencies = [...new Set(outstanding.map(inv => inv.currency || 'GBP'))];
  const multiCurrency = currencies.length > 1;
  const headerCurrency = multiCurrency ? 'Multi-currency' : currencies[0] || 'GBP';

  let y = drawBrandedHeader(doc, {
    title: 'Bonjour Residences',
    scopeLine: `Outstanding Statement: ${client} (${headerCurrency})`,
  });

  // Section title
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`Outstanding Invoices (${outstanding.length})`, 14, y + 4);
  y += 7;

  const fmtNative = (amount, currency) => {
    const sym = currencySymbol(currency || 'GBP');
    if (amount == null) return '';
    return `${amount < 0 ? '-' : ''}${sym}${Math.abs(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const headers = multiCurrency
    ? ['Invoice', 'Issue Date', 'Due Date', 'Currency', 'Amount', 'Paid', 'Outstanding', 'Overdue']
    : ['Invoice', 'Issue Date', 'Due Date', 'Amount', 'Paid', 'Outstanding', 'Overdue'];

  const rows = outstanding.map(inv => {
    const cur = inv.currency || 'GBP';
    const amt = inv.total_amount || 0;
    const paid = invoicePaidAmount(inv);
    const row = [
      inv.bros_id || '-',
      inv.issue_date || '-',
      inv.due_date || '-',
    ];
    if (multiCurrency) row.push(cur);
    row.push(fmtNative(amt, cur), fmtNative(paid, cur), fmtNative(amt - paid, cur));
    row.push(deriveInvoiceStatus(inv) === 'overdue' ? 'Yes' : '');
    return row;
  });

  // Per-currency subtotals (no cross-currency sum)
  for (const cur of currencies) {
    const subset = outstanding.filter(inv => (inv.currency || 'GBP') === cur);
    const totAmt = subset.reduce((s, inv) => s + (inv.total_amount || 0), 0);
    const totPaid = subset.reduce((s, inv) => s + invoicePaidAmount(inv), 0);
    const label = multiCurrency ? `Total (${cur})` : 'Total';
    const row = [
      { content: label, styles: { fontStyle: 'bold' } },
      '', '',
    ];
    if (multiCurrency) row.push('');
    row.push(
      { content: fmtNative(totAmt, cur), styles: { fontStyle: 'bold' } },
      { content: fmtNative(totPaid, cur), styles: { fontStyle: 'bold' } },
      { content: fmtNative(totAmt - totPaid, cur), styles: { fontStyle: 'bold' } },
      '',
    );
    rows.push(row);
  }

  autoTable(doc, {
    ...TABLE_DEFAULTS,
    startY: y,
    head: [headers],
    body: rows,
    columnStyles: { 0: { fontStyle: 'bold' } },
  });

  drawBrandedFooter(doc);

  const slug = slugifyClient(client);
  doc.save(`statement-${slug}.pdf`);
}

export async function exportClientStatementExcel({ client, invoices }) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const round2 = (v) => v != null ? Math.round(v * 100) / 100 : '';
  const CUR = EXCEL_CURRENCY_FORMAT;
  const generatedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  const outstanding = getOutstandingInvoices(invoices);

  // Determine currencies present (native, not display toggle)
  const currencies = [...new Set(outstanding.map(inv => inv.currency || 'GBP'))];
  const multiCurrency = currencies.length > 1;
  const headerCurrency = multiCurrency ? 'Multi-currency' : currencies[0] || 'GBP';

  const headers = multiCurrency
    ? ['Invoice', 'Issue Date', 'Due Date', 'Currency', 'Amount', 'Paid', 'Outstanding', 'Overdue']
    : ['Invoice', 'Issue Date', 'Due Date', 'Amount', 'Paid', 'Outstanding', 'Overdue'];

  // Currency column offsets: amounts start at col 4 for multi, col 3 for single
  const amtCol = multiCurrency ? 4 : 3;

  const aoa = [
    [`Outstanding Statement: ${client}`],
    [`Currency: ${headerCurrency}`, '', `Generated: ${generatedOn}`],
    [],
    headers,
  ];

  for (const inv of outstanding) {
    const cur = inv.currency || 'GBP';
    const amt = round2(inv.total_amount || 0);
    const paid = round2(invoicePaidAmount(inv));
    const row = [
      inv.bros_id || '-',
      inv.issue_date || '-',
      inv.due_date || '-',
    ];
    if (multiCurrency) row.push(cur);
    row.push(amt, paid, round2(amt - paid));
    row.push(deriveInvoiceStatus(inv) === 'overdue' ? 'Yes' : '');
    aoa.push(row);
  }

  // Per-currency subtotals (no cross-currency sum)
  for (const cur of currencies) {
    const subset = outstanding.filter(inv => (inv.currency || 'GBP') === cur);
    const totAmt = round2(subset.reduce((s, inv) => s + (inv.total_amount || 0), 0));
    const totPaid = round2(subset.reduce((s, inv) => s + invoicePaidAmount(inv), 0));
    const label = multiCurrency ? `Total (${cur})` : 'Total';
    const row = [label, '', ''];
    if (multiCurrency) row.push('');
    row.push(totAmt, totPaid, round2(totAmt - totPaid), '');
    aoa.push(row);
  }

  const fmtCols = {};
  fmtCols[amtCol] = CUR;
  fmtCols[amtCol + 1] = CUR;
  fmtCols[amtCol + 2] = CUR;
  addFormattedSheet(wb, XLSX, 'Outstanding Statement', aoa, { formats: fmtCols });

  const slug = slugifyClient(client);
  XLSX.writeFile(wb, `statement-${slug}.xlsx`);
}
